import { trackMixpanelSearchResultClicked } from '@/lib/mixpanel/mixpanel';
import { trackSearchResultClicked } from '@/pendo';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { modalState } from '@/state/modal.state';

import { useAddRecentSearchRecord } from './user-recent-search/use-add-recent-search-record.mutation';
import { downloadLinkAutomatically } from '@/helpers/download-link-automatically';
import { fetchTeamFileLink } from '@/api/v4/team-files.api';
import { type SearchSuggestion } from '@/api/v4/search.api';
import { fetchOrganizationFileLink } from '@/api/v4/organization-files.api';
import { fetchProjectFileLink } from '@/api/v4/project-files.api';
import { type SearchResultsFilter } from '../search.state';
import { useProjectListingsSectionParams } from '@/features/projects/project-listings/use-project-listings-section-params';
import { useSetAtom } from 'jotai';

export type SearchedListItem = {
  entrySurface?: string;
  id: string;
  type: Extract<
    SearchResultsFilter,
    'organization' | 'project' | 'team' | 'tag' | 'file'
  >;
  isRecent?: boolean;
};

export const useSearchedItemClick = () => {
  const navigateTo = useNavigateTo();
  const setModal = useSetAtom(modalState);
  const { addRecentSearchRecord } = useAddRecentSearchRecord();
  const listingsSectionParams = useProjectListingsSectionParams();

  const getFileLink = async (
    entityId: string,
    fileId: string,
    type: SearchSuggestion['entityType'],
  ) => {
    switch (type) {
      case 'team':
        return fetchTeamFileLink(
          { teamId: entityId, fileId: fileId },
          { mode: 'preview' },
        );

      case 'organization':
        return fetchOrganizationFileLink(
          { organizationId: entityId, fileId: fileId },
          { mode: 'preview' },
        );

      case 'project':
        return fetchProjectFileLink(
          { projectId: entityId, fileId: fileId },
          { mode: 'preview' },
        );
    }

    return null;
  };

  const previewFileOnNewTab = async (suggestion: SearchSuggestion) => {
    if (!suggestion.entityId) return;
    const link = await getFileLink(
      suggestion.entityId,
      suggestion.id,
      suggestion.entityType,
    );

    if (!link) return;

    await downloadLinkAutomatically(link, suggestion.name);
  };

  return async (
    { entrySurface, type, id, isRecent }: SearchedListItem,
    suggestion?: SearchSuggestion,
  ) => {
    if (type !== 'file') {
      setModal(null);
    }

    if (type !== 'tag' && type !== 'file') {
      if (type === 'organization' || type === 'project' || type === 'team') {
        const surface = entrySurface ?? 'global_search_all';
        trackMixpanelSearchResultClicked({
          entity_id: id,
          entity_type: type === 'organization' ? 'company' : type,
          entry_surface: surface,
          is_recent: isRecent ?? false,
        });
        trackSearchResultClicked({
          type: type === 'organization' ? 'company' : type,
          id,
          isRecent: isRecent ?? false,
        });
      }
      await addRecentSearchRecord({
        searchRecordId: id,
        type: (type === 'organization' ? 'company' : type) as
          | 'company'
          | 'project'
          | 'team',
      });
    }

    switch (type) {
      case 'organization':
        navigateTo.company({ companyId: id });
        break;
      case 'project':
        navigateTo.project({ projectId: id }, listingsSectionParams);
        break;
      case 'team':
        navigateTo.team({ teamId: id });
        break;
      case 'file':
        if (!suggestion) return;
        await previewFileOnNewTab(suggestion);
        break;
    }
  };
};
